import { HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from './contexts/AuthContext'
import LandingPage from './pages/LandingPage'
import Dashboard from './pages/Dashboard'
import StudentDashboard from './pages/StudentDashboard'
import StudentCatalogue from './pages/StudentCatalogue'
import TeacherCatalogue from './pages/TeacherCatalogue'
import TeacherClasses from './pages/TeacherClasses'
import TeacherAssignments from './pages/TeacherAssignments'
import TeacherInsights from './pages/TeacherInsights'
import TeacherLiveSessions from './pages/TeacherLiveSessions'
import TeacherLiveRemote from './pages/TeacherLiveRemote'
import Editor from './pages/Editor'
import ParcoursImagesEditor from './pages/ParcoursImagesEditor'
import ParcoursCoverage from './pages/ParcoursCoverage'
import Player from './pages/Player'
import CodexAdmin from './pages/CodexAdmin'
import { getCodexLinkMappings } from './services/codexLinkMappingsService'
import { setCodexDynamicMappings } from './utils/codexContextLinks'
import BetaGlobalBanner from './components/BetaGlobalBanner'
import EmailVerificationGraceBanner from './components/EmailVerificationGraceBanner'

function RouteLoading({ message = 'Chargement...' }) {
  return (
    <div className="app">
      <div className="container">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>{message}</p>
        </div>
      </div>
    </div>
  )
}

function PrivateRoute({ children, requiredRole = null, requireAdminTeacher = false }) {
  const { user, userData, loading, profileLoading, isGuest, isAdminTeacher } = useAuth()
  
  if (loading) {
    return <RouteLoading />
  }

  if (user && profileLoading && !userData) {
    return <RouteLoading message="Connexion en cours…" />
  }
  
  // Autoriser l'accès si mode invité (pour /student-dashboard uniquement)
  if (isGuest && !requiredRole) {
    return children
  }
  
  if (!user && !isGuest) {
    return <Navigate to="/" replace />
  }
  
  // Vérifier le rôle si requis (attendre le profil Firestore pour ne pas renvoyer à l’accueil)
  if (requiredRole && userData && userData.role !== requiredRole) {
    // Rediriger selon le rôle
    if (userData?.role === 'student') {
      return <Navigate to="/student-dashboard" replace />
    } else {
      return <Navigate to="/" replace />
    }
  }

  if (requireAdminTeacher && !isAdminTeacher) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

function PublicRoute({ children }) {
  const { user, userData, loading, profileLoading, isGuest } = useAuth()
  
  if (loading) {
    return <RouteLoading />
  }

  if (user && (profileLoading || !userData)) {
    return <RouteLoading message={profileLoading ? 'Connexion en cours…' : 'Chargement du profil…'} />
  }
  
  // Si en mode invité, rediriger vers le dashboard élève
  if (isGuest && !user) {
    return <Navigate to="/student-dashboard" replace />
  }
  
  // Si connecté, rediriger vers le bon dashboard
  if (user && userData) {
    if (userData.role === 'teacher') {
      return <Navigate to="/dashboard" replace />
    } else {
      return <Navigate to="/student-dashboard" replace />
    }
  }
  
  return children
}

function AppRouter() {
  useEffect(() => {
    getCodexLinkMappings()
      .then((mappings) => setCodexDynamicMappings(mappings))
      .catch(() => setCodexDynamicMappings(null))
  }, [])

  return (
    <HashRouter>
      <BetaGlobalBanner />
      <EmailVerificationGraceBanner />
      <Routes>
        {/* Route publique : Landing Page */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        
        {/* Routes professeur */}
        <Route path="/dashboard" element={<PrivateRoute requiredRole="teacher"><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/students" element={<PrivateRoute requiredRole="teacher"><StudentCatalogue /></PrivateRoute>} />
        <Route path="/dashboard/classes" element={<PrivateRoute requiredRole="teacher"><TeacherClasses /></PrivateRoute>} />
        <Route path="/dashboard/assignments" element={<PrivateRoute requiredRole="teacher"><TeacherAssignments /></PrivateRoute>} />
        <Route path="/dashboard/live-sessions" element={<PrivateRoute requiredRole="teacher"><TeacherLiveSessions /></PrivateRoute>} />
        <Route path="/dashboard/live-sessions/:sessionId/remote" element={<PrivateRoute requiredRole="teacher"><TeacherLiveRemote /></PrivateRoute>} />
        <Route path="/dashboard/insights" element={<PrivateRoute requiredRole="teacher"><TeacherInsights /></PrivateRoute>} />
        <Route path="/dashboard/teachers" element={<PrivateRoute requiredRole="teacher"><TeacherCatalogue /></PrivateRoute>} />
        <Route path="/editor" element={<PrivateRoute requiredRole="teacher"><Editor /></PrivateRoute>} />
        <Route path="/editor/:id" element={<PrivateRoute requiredRole="teacher"><Editor /></PrivateRoute>} />
        <Route path="/dashboard/parcours-images" element={<PrivateRoute requiredRole="teacher"><ParcoursImagesEditor /></PrivateRoute>} />
        <Route path="/dashboard/parcours-coverage" element={<PrivateRoute requiredRole="teacher"><ParcoursCoverage /></PrivateRoute>} />
        <Route path="/dashboard/codex-admin" element={<PrivateRoute requiredRole="teacher" requireAdminTeacher={true}><CodexAdmin /></PrivateRoute>} />
        
        {/* Routes élève */}
        <Route path="/student-dashboard" element={<PrivateRoute><StudentDashboard /></PrivateRoute>} />
        
        {/* Route publique : Player (accessible sans connexion) */}
        <Route path="/play/:exerciseId" element={<Player />} />
        
        {/* Route par défaut */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  )
}

export default AppRouter

